import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
  type SetStateAction,
} from "react"
import type { ConversationPartApiResponse } from "@f2e/ui/chat-v2"
import {
  ArrowDown,
  ArrowLeft,
  ChevronDown,
  ListTodo,
  Loader2,
  MessagesSquare,
  RefreshCw,
} from "lucide-react"
import { useTranslation } from "react-i18next"

import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@f2e/ui/components/base"
import type { AutosizeTextAreaRef } from "@f2e/ui/components/base-plus"
import { ScrollToBottomButton } from "@f2e/ui/components/Button"
import { useScrollToBottom } from "@f2e/ui/hooks"
import { cn } from "@f2e/ui/lib/utils"

import { WebConversationPartPanel } from "@/components/Conversation"
import { CopilotHiddenField } from "@/components/Copilot/CopilotHiddenField"
import { PermissionButton } from "@/components/Auth"
import {
  AIDraftVersionSwitcher,
  type AIDraftContextAnchor,
  type AppliedDraftComposerState,
} from "@/components/History/AIDraft"
import { AIDraftPlatformReplyComposer } from "@/components/History/AIDraftPlatformReplyComposer"
import { buildAIDraftTimelineGroups } from "@/components/History/aiDraftTimelineGroups"
import {
  getPlatformReplyActorDisplayName,
  getPlatformReplyErrorMessage,
  isPartialPlatformReply,
  hasSentPlatformReply,
} from "@/components/History/platformReplyMetadata"
import { buildPlatformReplyRequest } from "@/components/History/platformReplyRequest"
import { PlatformReplyAttachment } from "@/components/History/PlatformReplyAttachment"
import { PlatformReplyComposerMeta } from "@/components/History/PlatformReplyComposerMeta"
import { LineQuotaNotice } from "@/components/History/LineQuotaNotice"
import {
  threadAIDraftTarget,
  threadPartResponseToConversationPart,
  useCreateThreadPlatformReplyApi,
  useGetThreadApi,
  useGetThreadPartsApi,
  useListAIDraftsApi,
  useUpdateThreadStateApi,
  useUpdateThreadReadStateApi,
  type AIDraftSummaryResponse,
  type PlatformReplyResponse,
  type ThreadDetailResponse,
  type ThreadHistoryBoundaryResponse,
  type ThreadStatus,
  type ThreadSummaryResponse,
} from "@/hooks/api"
import { useToast, useSysConfig } from "@/hooks"
import { useAdaptivePolling } from "@/hooks/useAdaptivePolling"
import { FetchStatus } from "@/hooks/useFetch"
import {
  downloadAttachmentResource,
  loadAttachmentPreviewResource,
  resolveAttachmentResourceUrl,
} from "@/lib/attachmentResource"
import {
  getUserFacingApiErrorMessage,
  getUserFacingApiErrorToastProps,
} from "@/lib/apiErrors"
import { Permission, type Workspace } from "@/types"

import ThreadHistoryBoundary from "./ThreadHistoryBoundary"
import { ThreadReplyUsage } from "./ThreadReplyUsage"
import { ThreadCrmNotes } from "./ThreadCrmNotes"
import ThreadSessionSheet, {
  type ThreadSessionChange,
} from "./ThreadSessionSheet"
import { equalThreadData, mergeThreadParts } from "./threadListState"

const PROVIDER_REPLY_CHANNELS = new Set([
  "line",
  "messenger",
  "instagram",
  "slack",
])
const THREAD_POLL_FAILURE_VISIBLE_THRESHOLD = 3
const THREAD_POLL_JITTER_RANGE = { min: 0.9, max: 1 } as const
const ONE_MINUTE_MS = 60_000
const THREE_MINUTES_MS = 3 * ONE_MINUTE_MS
const FIVE_MINUTES_MS = 5 * ONE_MINUTE_MS

export const getThreadDetailPollingDelayMs = ({
  hasSendingReply,
  canReply,
  idleDurationMs,
}: {
  hasSendingReply: boolean
  canReply: boolean
  idleDurationMs: number
}) => {
  if (hasSendingReply) {
    if (idleDurationMs < ONE_MINUTE_MS) return 2_500
    if (idleDurationMs < THREE_MINUTES_MS) return 5_000
    return 15_000
  }
  if (canReply) {
    if (idleDurationMs < ONE_MINUTE_MS) return 5_000
    if (idleDurationMs < FIVE_MINUTES_MS) return 15_000
    return 30_000
  }
  if (idleDurationMs < ONE_MINUTE_MS) return 10_000
  if (idleDurationMs < FIVE_MINUTES_MS) return 15_000
  return 30_000
}

const isReplyResponse = (value: unknown): value is PlatformReplyResponse =>
  !!value &&
  typeof value === "object" &&
  "conversation_part_id" in value &&
  "history_id" in value

const getReplyStatus = (part: ConversationPartApiResponse) => {
  const reply = part.metadata.platform_reply
  if (!reply || typeof reply !== "object") return undefined
  const status = (reply as { status?: unknown }).status
  return typeof status === "string" ? status : undefined
}

const buildThreadListSyncSnapshot = (
  detail: ThreadDetailResponse | undefined,
  parts: ConversationPartApiResponse[]
) => {
  const latestVisiblePart = parts.findLast(
    (part) =>
      (part.part_kind === "user-prompt" || part.part_kind === "text") &&
      (part.metadata.source !== "platform_reply" ||
        getReplyStatus(part) === "sent")
  )
  return JSON.stringify({
    latestPart: latestVisiblePart
      ? {
          id:
            latestVisiblePart.id ??
            `${latestVisiblePart.history_id}:${latestVisiblePart.conversation_group_id}:${latestVisiblePart.sequence}`,
          updatedAt:
            latestVisiblePart.updated_at ?? latestVisiblePart.created_at,
          replyStatus: getReplyStatus(latestVisiblePart),
        }
      : null,
    summary: detail
      ? {
          title: detail.title,
          latestHistoryId: detail.latest_history_id,
          lastActivityAt: detail.last_activity_at,
          historyCount: detail.history_count,
          agentId: detail.agent_id,
          agentName: detail.agent_name,
          participantName: detail.participant_name,
          channel: detail.channel,
          latestMessage: detail.latest_message,
          latestMessagePreview: detail.latest_message_preview,
          status: detail.status,
          needsReply: detail.needs_reply,
          waitingSince: detail.waiting_since,
          feedbackConversationCounts: detail.feedback_conversation_counts,
          totalCostCredits: detail.total_cost_credits,
          humanMode: detail.human_mode,
          canReply: detail.can_reply,
          replyDisabledReason: detail.reply_disabled_reason,
        }
      : null,
  })
}

const threadDetailToSummary = ({
  histories: _histories,
  ...summary
}: ThreadDetailResponse): ThreadSummaryResponse => summary

const createReplyIdempotencyKey = () =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`

const isConsoleOnlyAIDraftToolPart = (part: ConversationPartApiResponse) =>
  (part.part_kind === "tool-call" || part.part_kind === "tool-return") &&
  part.metadata.source === "ai_draft" &&
  part.metadata.visibility === "console_only"

const resolveThreadReplyContextAnchor = (
  parts: ConversationPartApiResponse[]
): AIDraftContextAnchor | null => {
  const excludedGroups = new Set(
    parts
      .filter(
        (part) =>
          part.metadata.source === "platform_reply" &&
          getReplyStatus(part) !== "sent"
      )
      .map((part) => part.conversation_group_id)
  )
  const anchor = parts
    .filter(
      (part) =>
        part.id != null &&
        part.id > 0 &&
        part.source === "stack" &&
        part.part_kind !== "system-prompt" &&
        !excludedGroups.has(part.conversation_group_id)
    )
    .at(-1)
  return anchor?.id != null
    ? { part_id: anchor.id, sequence: anchor.sequence }
    : null
}

export interface ThreadDetailProps {
  organizationId: string
  workspace: Workspace
  threadKey: string
  readIntentToken?: number | null
  anchorHistoryId?: number | null
  onBack?: () => void
  onClearAnchor: () => void
  getThreadSummaryRevision: () => number
  onThreadSummaryChanged: (
    summary: ThreadSummaryResponse,
    revision: number
  ) => void
  onThreadListInvalidated: () => void
  onThreadStateChanged: (threadKey: string, status: ThreadStatus) => void
  onThreadStatusObserved: (
    threadKey: string,
    status: ThreadStatus,
    revision: number
  ) => void
  onReadStateChanged: (threadKey: string, isUnread: boolean) => void
  onThreadRemoved: () => void
  onCrmOpenChange?: (open: boolean) => void
}

const ThreadDetail = ({
  organizationId,
  workspace,
  threadKey,
  readIntentToken = null,
  anchorHistoryId = null,
  onBack,
  onClearAnchor,
  getThreadSummaryRevision,
  onThreadSummaryChanged,
  onThreadListInvalidated,
  onThreadStateChanged,
  onThreadStatusObserved,
  onReadStateChanged,
  onThreadRemoved,
  onCrmOpenChange,
}: ThreadDetailProps) => {
  const { t } = useTranslation()
  const { successToast, alertToast } = useToast()
  const sysConfig = useSysConfig()
  const requestScopeRef = useRef(0)
  const detailRequestGenerationRef = useRef(0)
  const loadedDetailRevisionRef = useRef(0)
  const activeThreadKeyRef = useRef<string | null>(threadKey)
  const positionedAnchorRef = useRef<string | null>(null)
  const pendingReplyRef = useRef<{ content: string; key: string } | null>(null)
  const pendingSentPlatformReplyPartIdRef = useRef<number | null>(null)
  const observedThreadStatusRef = useRef<string | null>(null)
  const readAttemptKeyRef = useRef<string | null>(null)
  const readViewportRef = useRef<HTMLDivElement | null>(null)
  const initialPositioningRef = useRef(false)
  const atBottomRef = useRef(true)
  const pendingScrollRef = useRef<"initial" | "reply" | "follow" | null>(null)
  const unseenCustomerPartRef = useRef<number | null>(null)
  const [hasUnseenCustomerMessage, setHasUnseenCustomerMessage] =
    useState(false)
  const resetDetailPollingRef = useRef<() => void>(() => undefined)
  const replyTextareaRef = useRef<AutosizeTextAreaRef | null>(null)
  const [detail, setDetail] = useState<ThreadDetailResponse>()
  const [parts, setPartsState] = useState<ConversationPartApiResponse[]>([])
  const [boundaries, setBoundaries] = useState<ThreadHistoryBoundaryResponse[]>(
    []
  )
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [hasMore, setHasMore] = useState(false)
  const [replyText, setReplyText] = useState("")
  const [crmOpen, setCrmOpen] = useState(false)
  const handleCrmOpenChange = useCallback(
    (open: boolean) => {
      setCrmOpen(open)
      onCrmOpenChange?.(open)
    },
    [onCrmOpenChange]
  )
  const [appliedDraftContext, setAppliedDraftContext] =
    useState<AppliedDraftComposerState | null>(null)
  const [aiDraftSummaries, setAIDraftSummaries] = useState<
    AIDraftSummaryResponse[]
  >([])
  const [threadLoadNotice, setThreadLoadNotice] = useState<string | null>(null)
  const [replyNotice, setReplyNotice] = useState<string | null>(null)
  const [sessionHistoryId, setSessionHistoryId] = useState<number | null>(null)
  const [isDeletingSession, setIsDeletingSession] = useState(false)
  const [isReconcilingLatestDeletion, setIsReconcilingLatestDeletion] =
    useState(false)
  const [initialDetailThreadKey, setInitialDetailThreadKey] = useState<
    string | null
  >(null)
  const [initialPartsThreadKey, setInitialPartsThreadKey] = useState<
    string | null
  >(null)
  const detailStateRef = useRef(detail)
  const partsStateRef = useRef(parts)
  activeThreadKeyRef.current = threadKey
  detailStateRef.current = detail
  // Keep request completions and local mutations on the same latest snapshot,
  // including updates queued before React's next render.
  const setParts = useCallback(
    (update: SetStateAction<ConversationPartApiResponse[]>) => {
      const next =
        typeof update === "function" ? update(partsStateRef.current) : update
      partsStateRef.current = next
      setPartsState(next)
    },
    []
  )

  useEffect(() => {
    activeThreadKeyRef.current = threadKey
    return () => {
      if (activeThreadKeyRef.current === threadKey) {
        activeThreadKeyRef.current = null
      }
    }
  }, [threadKey])

  const { error: detailError, fetchFn: getThread } = useGetThreadApi(
    organizationId,
    workspace.id,
    threadKey
  )
  const { fetchFn: getThreadInBackground } = useGetThreadApi(
    organizationId,
    workspace.id,
    threadKey
  )
  const {
    error: partsError,
    status: partsStatus,
    fetchFn: getParts,
  } = useGetThreadPartsApi(organizationId, workspace.id, threadKey)
  const { fetchFn: getPartsInBackground } = useGetThreadPartsApi(
    organizationId,
    workspace.id,
    threadKey
  )
  const { fetchFn: updateReadState } = useUpdateThreadReadStateApi(
    organizationId,
    workspace.id,
    threadKey
  )
  const { status: replyStatus, fetchFn: createReply } =
    useCreateThreadPlatformReplyApi(organizationId, workspace.id, threadKey)
  const { status: stateUpdateStatus, fetchFn: updateThreadState } =
    useUpdateThreadStateApi(organizationId, workspace.id, threadKey)
  const isReplySubmitting = replyStatus === FetchStatus.Loading
  const draftTarget = useMemo(
    () => threadAIDraftTarget(organizationId, workspace.id, threadKey),
    [organizationId, threadKey, workspace.id]
  )
  const { fetchFn: listAIDrafts } = useListAIDraftsApi(draftTarget)

  const loadDetail = useCallback(
    async ({
      background = false,
      signal,
    }: { background?: boolean; signal?: AbortSignal } = {}) => {
      const scope = requestScopeRef.current
      const generation = ++detailRequestGenerationRef.current
      const revision = getThreadSummaryRevision()
      const fetchThread = background ? getThreadInBackground : getThread
      const response = await fetchThread({
        cache: "no-store",
        preserveData: true,
        signal,
      }).catch(() => undefined)
      if (
        scope !== requestScopeRef.current ||
        generation !== detailRequestGenerationRef.current ||
        !response?.data
      )
        return false
      setDetail((current) =>
        equalThreadData(current, response.data) ? current : response.data
      )
      loadedDetailRevisionRef.current = revision
      setBoundaries((current) =>
        equalThreadData(current, response.data!.histories)
          ? current
          : response.data!.histories
      )
      setIsReconcilingLatestDeletion(false)
      if (!background) setInitialDetailThreadKey(threadKey)
      return { detail: response.data, revision }
    },
    [getThread, getThreadInBackground, getThreadSummaryRevision, threadKey]
  )

  const handleTogglePending = useCallback(async () => {
    if (!detail || stateUpdateStatus === FetchStatus.Loading) return
    const nextStatus: ThreadStatus =
      detail.status === "pending" ? "default" : "pending"

    try {
      const response = await updateThreadState({
        jsonBody: { status: nextStatus },
      })
      if (!response?.data) return
      setDetail((current) =>
        current ? { ...current, status: response.data!.status } : current
      )
      successToast({
        description: t(
          nextStatus === "pending"
            ? "historyList.thread.state.markedPending"
            : "historyList.thread.state.clearedPending"
        ),
      })
      onThreadStateChanged(threadKey, response.data.status)
      void loadDetail({ background: true })
      resetDetailPollingRef.current()
    } catch (error) {
      alertToast({
        ...getUserFacingApiErrorToastProps(error, t, {
          title: t("historyList.thread.state.updateFailed"),
          fallbackDescription: t(
            "historyList.thread.state.updateFailedDescription"
          ),
        }),
      })
    }
  }, [
    alertToast,
    detail,
    loadDetail,
    onThreadStateChanged,
    stateUpdateStatus,
    successToast,
    t,
    updateThreadState,
    threadKey,
  ])

  const loadParts = useCallback(
    async ({
      before,
      replace = false,
      updateCursor = true,
      background = false,
      signal,
    }: {
      before?: string
      replace?: boolean
      updateCursor?: boolean
      background?: boolean
      signal?: AbortSignal
    } = {}) => {
      const scope = requestScopeRef.current
      const searchParams = new URLSearchParams({ limit: "100" })
      if (before) searchParams.set("before", before)
      const fetchParts = background ? getPartsInBackground : getParts
      const response = await fetchParts({
        cache: "no-store",
        preserveData: true,
        searchParams,
        signal,
      }).catch(() => undefined)
      if (scope !== requestScopeRef.current || !response?.data) return false
      const incoming = response.data.parts.map(
        threadPartResponseToConversationPart
      )
      const previous = partsStateRef.current
      const combined = replace ? incoming : mergeThreadParts(previous, incoming)
      if (!before) {
        const viewport = readViewportRef.current
        if (viewport) {
          atBottomRef.current =
            viewport.scrollTop + viewport.clientHeight >=
            viewport.scrollHeight - 10
        }
        if (replace) {
          pendingScrollRef.current = "initial"
        } else if (combined !== previous) {
          const knownIds = new Set(previous.map((part) => part.id))
          const latestCustomer = combined.findLast(
            (part) => part.part_kind === "user-prompt" && part.id != null
          )
          const hasNewCustomer =
            latestCustomer?.id != null && !knownIds.has(latestCustomer.id)
          if (hasNewCustomer) {
            initialPositioningRef.current = false
            unseenCustomerPartRef.current = latestCustomer.id!
            setHasUnseenCustomerMessage(true)
            // Customer arrival takes precedence even when the same response
            // contains an agent reply. An explicit own reply still scrolls.
            if (pendingScrollRef.current !== "reply")
              pendingScrollRef.current = null
          } else if (
            atBottomRef.current &&
            unseenCustomerPartRef.current === null
          ) {
            const previousById = new Map(
              previous.map((part) => [part.id, part])
            )
            const hasReplyChange = incoming.some(
              (part) =>
                part.part_kind !== "user-prompt" &&
                !isConsoleOnlyAIDraftToolPart(part) &&
                (!previousById.has(part.id) ||
                  JSON.stringify(previousById.get(part.id)?.content) !==
                    JSON.stringify(part.content))
            )
            if (hasReplyChange && pendingScrollRef.current !== "reply")
              pendingScrollRef.current = "follow"
          }
        }
      }
      setParts(combined)
      setBoundaries((current) => {
        const merged = new Map(
          current.map((boundary) => [boundary.history_id, boundary])
        )
        let changed = false
        for (const boundary of response.data?.boundaries ?? []) {
          const previous = merged.get(boundary.history_id)
          if (equalThreadData(previous, boundary)) continue
          merged.set(boundary.history_id, boundary)
          changed = true
        }
        if (!changed) return current
        const next = [...merged.values()].sort((left, right) =>
          left.started_at.localeCompare(right.started_at)
        )
        return equalThreadData(current, next) ? current : next
      })
      if (updateCursor) {
        setNextCursor(response.data.next_cursor ?? null)
        setHasMore(response.data.has_more)
      }
      if (!background && replace && !before) {
        setInitialPartsThreadKey(threadKey)
      }
      return combined
    },
    [getParts, getPartsInBackground, threadKey, setParts]
  )

  const refreshLatestParts = useCallback(
    (background = false) =>
      loadParts({ replace: false, updateCursor: false, background }),
    [loadParts]
  )

  const loadAIDrafts = useCallback(async () => {
    const scope = requestScopeRef.current
    const limit = 100
    let offset = 0
    const summaries: AIDraftSummaryResponse[] = []
    try {
      while (true) {
        const response = await listAIDrafts({
          cache: "no-store",
          preserveData: true,
          searchParams: new URLSearchParams({
            limit: String(limit),
            offset: String(offset),
          }),
        })
        if (scope !== requestScopeRef.current) return
        summaries.push(...(response?.data ?? []))
        const total = response?.pagination?.total_records ?? summaries.length
        if (summaries.length >= total || (response?.data?.length ?? 0) < limit)
          break
        offset += limit
      }
      if (scope === requestScopeRef.current) setAIDraftSummaries(summaries)
    } catch (error) {
      if (scope === requestScopeRef.current)
        console.warn("failed to load Thread AI Draft records", error)
    }
  }, [listAIDrafts])

  useEffect(() => {
    requestScopeRef.current += 1
    setDetail(undefined)
    setParts([])
    setBoundaries([])
    setNextCursor(null)
    setHasMore(false)
    setReplyText("")
    setAppliedDraftContext(null)
    setAIDraftSummaries([])
    setThreadLoadNotice(null)
    setReplyNotice(null)
    setSessionHistoryId(null)
    setIsDeletingSession(false)
    setIsReconcilingLatestDeletion(false)
    setInitialDetailThreadKey(null)
    setInitialPartsThreadKey(null)
    positionedAnchorRef.current = null
    pendingSentPlatformReplyPartIdRef.current = null
    readAttemptKeyRef.current = null
    pendingScrollRef.current = null
    initialPositioningRef.current = false
    unseenCustomerPartRef.current = null
    setHasUnseenCustomerMessage(false)
    atBottomRef.current = true
    void loadDetail()
    void loadParts({ replace: true })
    void loadAIDrafts()
  }, [threadKey, loadAIDrafts, loadDetail, loadParts, setParts])

  useEffect(() => {
    if (!detail) return
    const observationKey = `${detail.id}:${detail.status}`
    if (observedThreadStatusRef.current === observationKey) return
    observedThreadStatusRef.current = observationKey
    onThreadStatusObserved(
      detail.id,
      detail.status,
      loadedDetailRevisionRef.current
    )
  }, [detail, onThreadStatusObserved])

  useEffect(() => {
    if (
      initialDetailThreadKey !== threadKey ||
      initialPartsThreadKey !== threadKey
    ) {
      return
    }
    const latestExternalPart = parts.findLast(
      (part) => part.part_kind === "user-prompt" && part.id != null
    )
    if (latestExternalPart?.id == null) return
    const throughPartId = latestExternalPart.id

    const message = readViewportRef.current?.querySelector<HTMLElement>(
      `[data-external-part-id="${throughPartId}"]`
    )
    if (!message) return

    const attemptKey = `${threadKey}:${readIntentToken}:${throughPartId}`
    const requestScope = requestScopeRef.current
    let active = true
    // Observe the actual message against the browser viewport, including all
    // scroll clipping ancestors. Loaded data alone is not evidence of reading.
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          !active ||
          document.visibilityState !== "visible" ||
          !document.hasFocus() ||
          !entries.some(
            (entry) =>
              entry.target === message &&
              entry.isIntersecting &&
              entry.intersectionRatio > 0
          )
        )
          return
        if (unseenCustomerPartRef.current === throughPartId) {
          unseenCustomerPartRef.current = null
          setHasUnseenCustomerMessage(false)
        }
        if (
          readIntentToken === null ||
          readAttemptKeyRef.current === attemptKey
        )
          return
        readAttemptKeyRef.current = attemptKey
        void updateReadState({
          jsonBody: { through_part_id: throughPartId },
        })
          .then((response) => {
            const readState = response?.data
            if (
              requestScope !== requestScopeRef.current ||
              readAttemptKeyRef.current !== attemptKey ||
              !readState
            )
              return
            setDetail((current) =>
              current ? { ...current, is_unread: readState.is_unread } : current
            )
            onReadStateChanged(threadKey, readState.is_unread)
          })
          .catch(() => undefined)
      },
      { threshold: 0 }
    )
    observer.observe(message)
    const recheckVisibility = () => {
      // Ask for a fresh measurement when returning to the app; a cached
      // intersection from before backgrounding may no longer be accurate.
      observer.unobserve(message)
      observer.observe(message)
    }
    window.addEventListener("focus", recheckVisibility)
    document.addEventListener("visibilitychange", recheckVisibility)
    return () => {
      active = false
      observer.disconnect()
      window.removeEventListener("focus", recheckVisibility)
      document.removeEventListener("visibilitychange", recheckVisibility)
    }
  }, [
    initialDetailThreadKey,
    initialPartsThreadKey,
    onReadStateChanged,
    parts,
    readIntentToken,
    threadKey,
    updateReadState,
  ])

  const firstPartIdByHistory = useMemo(() => {
    const first = new Map<number, string>()
    for (const part of parts) {
      if (isConsoleOnlyAIDraftToolPart(part)) continue
      if (first.has(part.history_id)) continue
      first.set(
        part.history_id,
        part.id == null
          ? `${part.conversation_group_id}:${part.sequence}`
          : `id:${part.id}`
      )
    }
    return first
  }, [parts])

  const replyContextAnchor = useMemo(
    () => resolveThreadReplyContextAnchor(parts),
    [parts]
  )
  const aiDraftTimelineGroups = useMemo(
    () => buildAIDraftTimelineGroups(aiDraftSummaries),
    [aiDraftSummaries]
  )

  const boundariesById = useMemo(
    () =>
      new Map(boundaries.map((boundary) => [boundary.history_id, boundary])),
    [boundaries]
  )

  const hasSendingReply = parts.some(
    (part) => getReplyStatus(part) === "sending"
  )
  const hasParts = parts.length > 0

  const {
    scrollContainerRef,
    showScrollToBottom,
    scrollToBottom,
    checkScrollPosition,
  } = useScrollToBottom({
    hasContent: hasParts,
    scrollToBottomOnMount: false,
    autoScroll: false,
  })

  const attachScrollContainer = useCallback(
    (node: HTMLDivElement | null) => {
      readViewportRef.current = node
      scrollContainerRef(node)
    },
    [scrollContainerRef]
  )

  useEffect(() => {
    if (!anchorHistoryId || parts.length === 0) return
    const anchorKey = `${threadKey}:${anchorHistoryId}`
    if (positionedAnchorRef.current === anchorKey) return
    const target = document.getElementById(`thread-history-${anchorHistoryId}`)
    if (!target) return
    target.scrollIntoView({ block: "center", behavior: "smooth" })
    positionedAnchorRef.current = anchorKey
  }, [anchorHistoryId, parts.length, threadKey])

  const handleDetailScroll = useCallback(() => {
    const viewport = readViewportRef.current
    if (!viewport) return
    atBottomRef.current =
      viewport.scrollTop + viewport.clientHeight >= viewport.scrollHeight - 10
  }, [])

  useLayoutEffect(() => {
    const viewport = readViewportRef.current
    const action = pendingScrollRef.current
    if (!viewport || !action) return
    pendingScrollRef.current = null
    if (action === "initial" && anchorHistoryId) return
    if (
      action === "follow" &&
      (anchorHistoryId ||
        !atBottomRef.current ||
        unseenCustomerPartRef.current !== null)
    )
      return
    if (action === "initial") initialPositioningRef.current = true
    viewport.scrollTo({ top: viewport.scrollHeight, behavior: "auto" })
    atBottomRef.current = true
    checkScrollPosition()
  }, [anchorHistoryId, checkScrollPosition, detail, parts])

  useEffect(() => {
    const viewport = readViewportRef.current
    const content = viewport?.firstElementChild
    if (!viewport || !content) return
    let active = true
    const observer = new ResizeObserver(() => {
      if (!active || !initialPositioningRef.current || anchorHistoryId) return
      viewport.scrollTo({ top: viewport.scrollHeight, behavior: "auto" })
      atBottomRef.current = true
      checkScrollPosition()
    })
    observer.observe(content)
    observer.observe(viewport)
    return () => {
      active = false
      observer.disconnect()
    }
  }, [anchorHistoryId, checkScrollPosition, detail])

  const stopInitialPositioning = useCallback(() => {
    initialPositioningRef.current = false
  }, [])

  const observePendingReplyDelivery = useCallback(
    (candidateParts: ConversationPartApiResponse[]) => {
      const pendingPartId = pendingSentPlatformReplyPartIdRef.current
      if (
        pendingPartId === null ||
        !hasSentPlatformReply(candidateParts, pendingPartId)
      ) {
        return false
      }

      pendingSentPlatformReplyPartIdRef.current = null
      return true
    },
    []
  )

  const pollThreadDetail = useCallback(
    async ({ signal }: { signal: AbortSignal }) => {
      if (replyStatus === FetchStatus.Loading) return "unchanged" as const

      const currentDetail = detailStateRef.current
      const currentParts = partsStateRef.current
      const currentListSnapshot = buildThreadListSyncSnapshot(
        currentDetail,
        currentParts
      )
      const [detailLoaded, partsLoaded] = await Promise.all([
        loadDetail({ background: true, signal }),
        loadParts({
          replace: false,
          updateCursor: false,
          background: true,
          signal,
        }),
      ])
      if (signal.aborted) return "unchanged" as const

      const nextDetail = detailLoaded ? detailLoaded.detail : currentDetail
      const nextParts = partsLoaded || currentParts
      if (partsLoaded) observePendingReplyDelivery(nextParts)
      const changed =
        Boolean(detailLoaded || partsLoaded) &&
        buildThreadListSyncSnapshot(nextDetail, nextParts) !==
          currentListSnapshot
      if (changed) {
        if (detailLoaded) {
          onThreadSummaryChanged(
            threadDetailToSummary(detailLoaded.detail),
            detailLoaded.revision
          )
        } else {
          onThreadListInvalidated()
        }
      }
      if (!detailLoaded || !partsLoaded) {
        throw new Error("Thread detail polling did not complete")
      }
      return changed ? ("changed" as const) : ("unchanged" as const)
    },
    [
      loadDetail,
      loadParts,
      observePendingReplyDelivery,
      onThreadListInvalidated,
      onThreadSummaryChanged,
      replyStatus,
    ]
  )

  const initialPollingDelayMs = getThreadDetailPollingDelayMs({
    hasSendingReply,
    canReply: detail?.can_reply === true,
    idleDurationMs: 0,
  })
  const {
    runNow: runThreadDetailPollNow,
    resetActivity: resetThreadDetailPollingActivity,
    isPolling: isThreadDetailPolling,
    consecutiveFailures: pollingFailureCount,
  } = useAdaptivePolling({
    enabled: Boolean(
      detail &&
      initialDetailThreadKey === threadKey &&
      initialPartsThreadKey === threadKey &&
      !isReconcilingLatestDeletion
    ),
    scopeKey: `${organizationId}:${workspace.id}:${threadKey}`,
    poll: pollThreadDetail,
    getDelayMs: ({ idleDurationMs }) =>
      getThreadDetailPollingDelayMs({
        hasSendingReply,
        canReply: detail?.can_reply === true,
        idleDurationMs,
      }),
    initialDelayMs: initialPollingDelayMs,
    jitterRange: THREAD_POLL_JITTER_RANGE,
  })
  resetDetailPollingRef.current = resetThreadDetailPollingActivity

  useEffect(() => {
    if (!detailError && !partsError) {
      setThreadLoadNotice(null)
      return
    }
    setThreadLoadNotice(
      getUserFacingApiErrorMessage(
        detailError || partsError,
        t,
        t("historyList.thread.loadFailedDescription")
      )
    )
  }, [detailError, partsError, t])

  const handleSendReply = useCallback(
    async (attachmentId?: string, onAccepted?: () => void) => {
      const content = replyText.trim()
      if ((!content && !attachmentId) || replyStatus === FetchStatus.Loading)
        return

      if (pendingReplyRef.current?.content !== content) {
        pendingReplyRef.current = { content, key: createReplyIdempotencyKey() }
      }
      const pending = pendingReplyRef.current
      const latestPart = parts.at(-1)
      const listSnapshotBeforeReply = buildThreadListSyncSnapshot(detail, parts)
      setThreadLoadNotice(null)
      setReplyNotice(null)

      try {
        const response = await createReply({
          jsonBody: {
            ...buildPlatformReplyRequest({
              content,
              attachmentId,
              idempotencyKey: pending.key,
              appliedDraftId: appliedDraftContext?.id ?? null,
              observedContextAnchor: replyContextAnchor,
              formPrefilledValues: appliedDraftContext?.form?.values ?? null,
            }),
          },
        })
        if (!isReplyResponse(response?.data)) {
          const message = response?.data?.message || response?.message
          setReplyNotice(
            message || t("history.platformReply.submitFailedFallback")
          )
          return
        }

        if (response.data.status === "failed") {
          pendingSentPlatformReplyPartIdRef.current = null
          pendingReplyRef.current = null
          setAppliedDraftContext(null)
          setReplyNotice(
            getPlatformReplyErrorMessage(response.data, t) ||
              response.data.error_code ||
              t("history.platformReply.deliveryFailedFallback")
          )
          await refreshLatestParts(true)
          await loadAIDrafts()
          const refreshedDetail = await loadDetail({ background: true })
          if (refreshedDetail) {
            onThreadSummaryChanged(
              threadDetailToSummary(refreshedDetail.detail),
              refreshedDetail.revision
            )
          }
          resetDetailPollingRef.current()
          return
        }

        pendingSentPlatformReplyPartIdRef.current =
          response.data.status === "sending"
            ? response.data.conversation_part_id
            : null
        const optimisticPart: ConversationPartApiResponse = {
          id: response.data.conversation_part_id,
          history_id: response.data.history_id,
          conversation_group_id: `platform-reply-${response.data.id}`,
          sequence: (latestPart?.sequence ?? 0) + 1,
          part_kind: "text",
          content: { content },
          metadata: {
            source: "platform_reply",
            platform_reply: {
              ...{
                id: response.data.id,
                attachment: response.data.attachment ?? null,
              },
              status: response.data.status,
              error_code: response.data.error_code,
              error_message: response.data.error_message,
            },
            delivery_channel: {
              channel_type: detail?.channel_type ?? undefined,
            },
          },
          source: "stack",
          created_at: response.data.created_at,
          updated_at: response.data.updated_at,
          attached_files: [],
        }
        pendingScrollRef.current = "reply"
        setParts((current) => mergeThreadParts(current, [optimisticPart]))
        pendingReplyRef.current = null
        setReplyText("")
        onAccepted?.()
        setAppliedDraftContext(null)
        successToast({
          description: t("historyList.thread.replyQueued"),
        })
        const refreshedParts = await refreshLatestParts()
        const nextParts = refreshedParts || partsStateRef.current
        const deliveredPendingReply = observePendingReplyDelivery(nextParts)
        await loadAIDrafts()
        const refreshedDetail = await loadDetail()
        const listSummaryChanged =
          response.data.status !== "sent" &&
          !deliveredPendingReply &&
          buildThreadListSyncSnapshot(
            refreshedDetail ? refreshedDetail.detail : detail,
            nextParts
          ) !== listSnapshotBeforeReply
        if (
          refreshedDetail &&
          (response.data.status === "sent" ||
            deliveredPendingReply ||
            listSummaryChanged)
        ) {
          onThreadSummaryChanged(
            threadDetailToSummary(refreshedDetail.detail),
            refreshedDetail.revision
          )
        } else if (
          response.data.status === "sent" ||
          deliveredPendingReply ||
          listSummaryChanged
        ) {
          onThreadListInvalidated()
        }
        resetDetailPollingRef.current()
      } catch (error) {
        setReplyNotice(
          getUserFacingApiErrorMessage(
            error,
            t,
            t("history.platformReply.submitFailedFallback")
          )
        )
      }
    },
    [
      createReply,
      appliedDraftContext,
      detail,
      loadDetail,
      loadAIDrafts,
      observePendingReplyDelivery,
      onThreadListInvalidated,
      onThreadSummaryChanged,
      parts,
      refreshLatestParts,
      replyStatus,
      replyContextAnchor,
      replyText,
      setParts,
      successToast,
      t,
    ]
  )

  const resolveAttachmentUrl = useCallback(
    (url: string) => resolveAttachmentResourceUrl(url, sysConfig.serverRoot),
    [sysConfig.serverRoot]
  )
  const handleAttachmentDownload = useCallback(
    (file: { url?: string; name: string }) =>
      downloadAttachmentResource(file, sysConfig.serverRoot),
    [sysConfig.serverRoot]
  )
  const handleAttachmentPreviewLoad = useCallback(
    (file: { url?: string; name: string }) =>
      loadAttachmentPreviewResource(file, sysConfig.serverRoot),
    [sysConfig.serverRoot]
  )

  const handleBackToLatest = () => {
    onClearAnchor()
    scrollToBottom()
  }

  const handleRetryLatestDeletion = () => {
    setThreadLoadNotice(null)
    void loadDetail()
    void refreshLatestParts()
    void loadAIDrafts()
  }

  const handleSessionChanged = ({ action, historyId }: ThreadSessionChange) => {
    if (activeThreadKeyRef.current !== threadKey) {
      onThreadListInvalidated()
      return
    }

    if (action === "deleted") {
      const removesThread = detail !== undefined && detail.history_count <= 1
      const deletesLatestHistory =
        detail?.latest_history_id === historyId && !removesThread
      if (deletesLatestHistory) {
        setIsReconcilingLatestDeletion(true)
        setReplyText("")
        setAppliedDraftContext(null)
        setReplyNotice(null)
        pendingReplyRef.current = null
        pendingSentPlatformReplyPartIdRef.current = null
      }
      // Ignore refreshes that started before deletion so stale responses cannot
      // merge the removed History back into the local timeline.
      requestScopeRef.current += 1
      setDetail((current) => {
        if (!current) return current
        const histories = current.histories.filter(
          (history) => history.history_id !== historyId
        )
        if (histories.length === current.histories.length) return current
        return { ...current, histories, history_count: histories.length }
      })
      setParts((current) =>
        current.filter((part) => part.history_id !== historyId)
      )
      setBoundaries((current) =>
        current.filter((boundary) => boundary.history_id !== historyId)
      )
      setSessionHistoryId((current) => (current === historyId ? null : current))
      if (anchorHistoryId === historyId) onClearAnchor()

      if (removesThread) {
        onThreadListInvalidated()
        onThreadRemoved()
        return
      }
    }
    void loadDetail()
    void refreshLatestParts()
    void loadAIDrafts()
    onThreadListInvalidated()
    resetDetailPollingRef.current()
  }

  const renderPart = useCallback<
    NonNullable<ComponentProps<typeof WebConversationPartPanel>["renderPart"]>
  >(
    (_context, part, defaultNode) => {
      if (isConsoleOnlyAIDraftToolPart(part)) return <></>
      const key =
        part.id == null
          ? `${part.conversation_group_id}:${part.sequence}`
          : `id:${part.id}`
      const boundary = boundariesById.get(part.history_id)
      const draftGroup =
        part.id == null
          ? undefined
          : aiDraftTimelineGroups.correlatedByPartId.get(part.id)
      const actualNode = (
        <>
          {defaultNode}
          <ThreadReplyUsage part={part} />
        </>
      )
      const contentNode = draftGroup ? (
        <AIDraftVersionSwitcher
          draftTarget={draftTarget}
          summaries={draftGroup}
          actualNode={actualNode}
        />
      ) : (
        actualNode
      )
      const timelineNode =
        part.part_kind === "user-prompt" && part.id != null ? (
          <div data-external-part-id={part.id}>{contentNode}</div>
        ) : (
          contentNode
        )
      if (!boundary || firstPartIdByHistory.get(part.history_id) !== key)
        return timelineNode
      return (
        <>
          <ThreadHistoryBoundary
            boundary={boundary}
            workspaceId={workspace.id}
            highlighted={anchorHistoryId === boundary.history_id}
            onOpenSession={setSessionHistoryId}
          />
          {timelineNode}
        </>
      )
    },
    [
      aiDraftTimelineGroups.correlatedByPartId,
      draftTarget,
      boundariesById,
      firstPartIdByHistory,
      workspace.id,
      anchorHistoryId,
    ]
  )

  const renderBetweenParts = useCallback<
    NonNullable<
      ComponentProps<typeof WebConversationPartPanel>["renderBetweenParts"]
    >
  >(
    (_context, part) => {
      const draftGroup =
        part.id == null
          ? undefined
          : aiDraftTimelineGroups.uncorrelatedByAnchorId.get(part.id)
      if (!draftGroup) return null
      return (
        <AIDraftVersionSwitcher
          draftTarget={draftTarget}
          summaries={draftGroup}
        />
      )
    },
    [aiDraftTimelineGroups.uncorrelatedByAnchorId, draftTarget]
  )

  const renderPartLabel = useCallback<
    NonNullable<
      ComponentProps<typeof WebConversationPartPanel>["renderPartLabel"]
    >
  >(
    (_context, part) => {
      if (part.metadata.source !== "platform_reply") return null
      const status = getReplyStatus(part) || "unknown"
      const partial = isPartialPlatformReply(part)
      const actorDisplayName = getPlatformReplyActorDisplayName(part)
      const errorMessage = getPlatformReplyErrorMessage(
        part.metadata.platform_reply,
        t
      )
      return (
        <div className="text-xs font-medium text-muted-foreground">
          {t("history.platformReply.humanOperatorMessage")} ·{" "}
          {actorDisplayName && <>{actorDisplayName} · </>}
          {t(`history.platformReply.status.${partial ? "partial" : status}`, {
            defaultValue: status,
          })}
          {(status === "failed" || partial) && errorMessage && (
            <span className="mt-1 block text-destructive">{errorMessage}</span>
          )}
          <PlatformReplyAttachment
            part={part}
            workspace={workspace}
            resolveUrl={resolveAttachmentUrl}
            canResend={detail?.can_reply === true}
            threadKey={threadKey}
            onChanged={() => void refreshLatestParts(true)}
          />
        </div>
      )
    },
    [
      t,
      workspace,
      detail?.can_reply,
      threadKey,
      refreshLatestParts,
      resolveAttachmentUrl,
    ]
  )

  if (detail === undefined && !detailError) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
        {t("historyList.thread.loading")}
      </div>
    )
  }

  if (!detail) {
    return (
      <div className="flex h-full items-center justify-center px-6 text-center text-sm text-muted-foreground">
        {threadLoadNotice || t("historyList.thread.loadFailedDescription")}
      </div>
    )
  }

  const isReplyCandidate = PROVIDER_REPLY_CHANNELS.has(
    detail.channel_type || ""
  )
  const isReplyBlockedBySessionDeletion =
    isDeletingSession || isReconcilingLatestDeletion

  return (
    <div className="flex h-full min-h-0 flex-col bg-background">
      <CopilotHiddenField fieldKey="thread.selectedId" value={detail.id} />
      <CopilotHiddenField
        fieldKey="thread.latestHistoryId"
        value={String(detail.latest_history_id)}
      />
      <CopilotHiddenField
        fieldKey="thread.agentId"
        value={detail.agent_id || ""}
      />
      <header className="z-20 shrink-0 border-b border-border/70 bg-background px-4 py-3 xl:px-5">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
          <div className="flex min-w-0 items-start gap-2">
            {onBack && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0"
                onClick={onBack}
                aria-label={t("historyList.detail.backToList")}
              >
                <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              </Button>
            )}
            <div className="min-w-0">
              <h2 className="line-clamp-2 text-base font-semibold leading-6 text-foreground">
                {detail.title?.trim() || t("historyList.thread.untitled")}
              </h2>
              <div className="mt-0.5 text-xs text-muted-foreground">
                {t("historyList.thread.sessionCount", {
                  count: detail.history_count,
                })}
                {detail.agent_name ? ` · ${detail.agent_name}` : ""}
              </div>
            </div>
          </div>

          <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
            <PermissionButton
              type="button"
              variant="outline"
              size="sm"
              className={cn(
                "h-8 gap-1.5 text-xs",
                detail.status === "pending" &&
                  "border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 hover:text-blue-800 dark:border-blue-900/60 dark:bg-blue-950/40 dark:text-blue-300 dark:hover:bg-blue-950/60 dark:hover:text-blue-200"
              )}
              permission={Permission.WsUpdateResources}
              workspace={workspace}
              disabled={stateUpdateStatus === FetchStatus.Loading}
              disabledTooltip={t("historyList.thread.state.permissionTooltip")}
              aria-pressed={detail.status === "pending"}
              onClick={() => void handleTogglePending()}
            >
              {stateUpdateStatus === FetchStatus.Loading ? (
                <Loader2
                  className="h-3.5 w-3.5 animate-spin"
                  aria-hidden="true"
                />
              ) : (
                <ListTodo className="h-3.5 w-3.5" aria-hidden="true" />
              )}
              {t(
                detail.status === "pending"
                  ? "historyList.thread.pending"
                  : "historyList.thread.state.markPending"
              )}
            </PermissionButton>

            <Popover>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 gap-1.5 text-xs"
                >
                  <MessagesSquare className="h-3.5 w-3.5" aria-hidden="true" />
                  {t("historyList.thread.sessions")}
                  <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 p-1.5">
                <div className="max-h-72 overflow-y-auto">
                  {detail.histories.map((boundary) => (
                    <button
                      type="button"
                      key={boundary.history_id}
                      className="block w-full rounded-md px-3 py-2 text-left hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      onClick={() => setSessionHistoryId(boundary.history_id)}
                    >
                      <div className="truncate text-sm font-medium">
                        {boundary.title?.trim() ||
                          t("historyList.thread.sessionTitle", {
                            id: boundary.history_id,
                          })}
                      </div>
                      <div className="mt-0.5 text-xs text-muted-foreground">
                        {new Date(boundary.started_at).toLocaleString()}
                      </div>
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </header>

      <div className="@container/crm relative flex min-h-0 flex-1 flex-col">
        <ThreadCrmNotes
          key={threadKey}
          organizationId={organizationId}
          workspace={workspace}
          onOpenChange={handleCrmOpenChange}
          clientUserId={
            initialDetailThreadKey === threadKey ? detail.client_user_id : null
          }
        />

        <div
          className={cn(
            "flex min-h-0 flex-1 flex-col",
            crmOpen && "@min-[760px]/crm:pr-80 @max-[760px]/crm:hidden"
          )}
        >
          {pollingFailureCount >= THREAD_POLL_FAILURE_VISIBLE_THRESHOLD && (
            <div
              role="status"
              className="flex shrink-0 items-center justify-between gap-3 border-b bg-muted/40 px-4 py-2.5 xl:px-5"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground">
                  {t("historyList.thread.syncDelayed")}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t("historyList.thread.syncDelayedDescription")}
                </p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 shrink-0 gap-1.5 text-xs"
                disabled={isThreadDetailPolling}
                onClick={() => runThreadDetailPollNow()}
              >
                <RefreshCw
                  className={cn(
                    "h-3.5 w-3.5",
                    isThreadDetailPolling && "animate-spin"
                  )}
                />
                {t("historyList.thread.retrySync")}
              </Button>
            </div>
          )}

          <div
            ref={attachScrollContainer}
            onScroll={handleDetailScroll}
            onWheel={stopInitialPositioning}
            onTouchMove={stopInitialPositioning}
            onPointerDown={stopInitialPositioning}
            onKeyDown={stopInitialPositioning}
            className="min-h-0 flex-1 overflow-y-auto overscroll-contain"
            data-scroll-container="thread-detail"
          >
            <div className="mx-auto w-full max-w-(--breakpoint-lg) px-4 py-4 xl:px-6">
              {hasMore && nextCursor && (
                <div className="mb-4 flex justify-center">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={partsStatus === FetchStatus.Loading}
                    onClick={() => void loadParts({ before: nextCursor })}
                  >
                    {partsStatus === FetchStatus.Loading && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    {t("historyList.thread.loadEarlier")}
                  </Button>
                </div>
              )}

              {parts.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border px-5 py-12 text-center">
                  <div className="text-sm font-medium text-foreground">
                    {t("historyList.thread.partialBackfillTitle")}
                  </div>
                  <div className="mx-auto mt-1 max-w-md text-sm leading-6 text-muted-foreground">
                    {t("historyList.thread.partialBackfillDescription")}
                  </div>
                  {detail.histories[0] && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mt-4"
                      onClick={() =>
                        setSessionHistoryId(detail.histories[0].history_id)
                      }
                    >
                      {t("historyList.thread.viewSession")}
                    </Button>
                  )}
                </div>
              ) : (
                <WebConversationPartPanel
                  className="w-full py-1"
                  parts={parts}
                  showExternalUserId={true}
                  externalUserIdFallback={detail.external_user_id ?? undefined}
                  assistantActionBarMode="hidden"
                  resolveUrl={resolveAttachmentUrl}
                  onDownload={handleAttachmentDownload}
                  onPreviewLoad={handleAttachmentPreviewLoad}
                  renderPart={renderPart}
                  renderBetweenParts={renderBetweenParts}
                  renderPartLabel={renderPartLabel}
                  copilotFieldPrefix="thread.conversation_part"
                />
              )}
            </div>
          </div>

          <div className="relative z-20 shrink-0 border-t border-border/70 bg-background px-4 pb-3 pt-2 xl:px-5">
            {hasUnseenCustomerMessage ? (
              <div
                role="status"
                aria-live="polite"
                className="absolute bottom-[calc(100%+8px)] left-1/2 z-30 -translate-x-1/2"
              >
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="gap-2 whitespace-nowrap rounded-full bg-background shadow-md"
                  onClick={handleBackToLatest}
                >
                  <ArrowDown className="h-4 w-4" aria-hidden="true" />
                  {t("historyList.thread.viewNewMessages")}
                </Button>
              </div>
            ) : (
              <ScrollToBottomButton
                className="z-30"
                show={showScrollToBottom}
                onClick={scrollToBottom}
                position="absolute"
                bottom="calc(100% + 8px)"
              />
            )}
            <div className="mx-auto w-full max-w-(--breakpoint-lg)">
              <LineQuotaNotice
                organizationId={organizationId}
                workspaceId={workspace.id}
                conversationKey={detail.id}
                channel={detail.channel}
              />
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <PlatformReplyComposerMeta
                  canSendPlatformReply={
                    detail.can_reply && !isReplyBlockedBySessionDeletion
                  }
                  canUseFollowUp={false}
                  isPollingRecovering={false}
                  replyDisabledReason={
                    isDeletingSession
                      ? t("historyList.thread.deletingSession")
                      : isReconcilingLatestDeletion
                        ? t("historyList.thread.refreshingAfterDelete")
                        : (detail.reply_disabled_reason ?? undefined)
                  }
                  platformReplyCandidate={isReplyCandidate}
                  composerMode="platformReply"
                  onComposerModeChange={() => undefined}
                  onOpenCrm={() =>
                    window.open(
                      detail.client_user_id
                        ? `/workspaces/${workspace.id}/users/${detail.client_user_id}`
                        : `/workspaces/${workspace.id}?tab=user-management`,
                      "_blank",
                      "noopener,noreferrer"
                    )
                  }
                />
                <span className="text-xs text-muted-foreground">
                  {t("historyList.thread.credits", {
                    credits: detail.total_cost_credits,
                  })}
                </span>
              </div>

              {anchorHistoryId && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="mb-2 h-8 gap-1.5 text-xs"
                  onClick={handleBackToLatest}
                >
                  <ArrowDown className="h-3.5 w-3.5" aria-hidden="true" />
                  {t("historyList.thread.backToLatest")}
                </Button>
              )}

              {(replyNotice || threadLoadNotice) && (
                <div
                  className="mb-2 space-y-1 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                  role="alert"
                >
                  {replyNotice && <div>{replyNotice}</div>}
                  {threadLoadNotice && <div>{threadLoadNotice}</div>}
                  {isReconcilingLatestDeletion && threadLoadNotice && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mt-1 h-7"
                      onClick={handleRetryLatestDeletion}
                    >
                      {t("historyList.thread.retryRefreshAfterDelete")}
                    </Button>
                  )}
                </div>
              )}

              {detail.can_reply && !isReplyBlockedBySessionDeletion && (
                <div className="rounded-xl border border-border/80 bg-background p-3 shadow-sm focus-within:ring-2 focus-within:ring-ring/20">
                  <AIDraftPlatformReplyComposer
                    draftTarget={draftTarget}
                    historyId={detail.latest_history_id}
                    channelType={detail.channel_type}
                    onAttachmentChange={() => {
                      pendingReplyRef.current = null
                    }}
                    message={replyText}
                    currentContextAnchor={replyContextAnchor}
                    appliedDraftContext={appliedDraftContext}
                    isSubmitting={isReplySubmitting}
                    unsupportedTools={
                      detail?.ai_draft_tool_support?.unsupported_tools
                    }
                    toolSupportStatus={
                      detail?.ai_draft_tool_support?.status ?? "unavailable"
                    }
                    textareaRef={replyTextareaRef}
                    placeholder={t("history.platformReply.placeholder")}
                    sendLabel={t("history.platformReply.send")}
                    submittingLabel={t("history.platformReply.sending")}
                    onMessageChange={(value) => {
                      pendingReplyRef.current = null
                      setReplyText(value)
                    }}
                    onAppliedDraftChange={(draft) => {
                      pendingReplyRef.current = null
                      setAppliedDraftContext(draft)
                    }}
                    onRecordsChanged={() => void loadAIDrafts()}
                    onBillingChanged={() => {
                      void loadDetail()
                      onThreadListInvalidated()
                      resetDetailPollingRef.current()
                    }}
                    onPermissionDenied={() => void loadDetail()}
                    onSend={(id, accepted) =>
                      void handleSendReply(id, accepted)
                    }
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <ThreadSessionSheet
        historyId={sessionHistoryId}
        workspace={workspace}
        deleteBlocked={replyStatus === FetchStatus.Loading}
        onOpenChange={(open) => {
          if (!open) setSessionHistoryId(null)
        }}
        onDeletePendingChange={setIsDeletingSession}
        onSessionChanged={handleSessionChanged}
      />
    </div>
  )
}

export default ThreadDetail
